import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bot, Loader, AlertCircle } from "lucide-react";
import { useAuthStore } from "../../store/authStore";
import SEO from "../SEO";

export default function PromptAdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showResetLink, setShowResetLink] = useState(false);
  const { signIn, user, loading, error } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate("/prompt-admin/prompts", { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await signIn(email, password);
    } catch (error) {
      console.error("Login failed:", error);
      setShowResetLink(true);
    }
  };

  const getErrorMessage = (error: string) => {
    if (error.includes("Invalid login credentials")) {
      return "The email or password you entered is incorrect. Please try again.";
    }
    if (error.includes("Access denied")) {
      return "This area is restricted to admin users only.";
    }
    return "An error occurred while signing in. Please try again.";
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <SEO
        title="One80Labs - Prompt Library Admin Login"
        description="Admin login for the One80Labs Prompt Library."
        noindex={true}
      />
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="bg-[#F98B3D] rounded-md p-2">
            <Bot className="h-12 w-12 text-white" />
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Prompt Library Admin
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Sign in to manage your prompt library
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {error && (
            <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-700">
                    {getErrorMessage(error)}
                  </p>
                  {showResetLink && (
                    <p className="text-sm text-gray-600 mt-2">
                      Forgot your password?{" "}
                      <a
                        href="https://app.supabase.com/reset-password"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-[#F98B3D] hover:text-[#e07a2c]"
                      >
                        Reset it here
                      </a>
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Email address
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#F98B3D] focus:border-[#F98B3D] sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                Password
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#F98B3D] focus:border-[#F98B3D] sm:text-sm"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className={`w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F98B3D] ${
                  loading ? "opacity-75 cursor-not-allowed" : ""
                }`}
              >
                {loading ? (
                  <>
                    <Loader className="animate-spin h-5 w-5 mr-2" />
                    Signing in...
                  </>
                ) : (
                  "Sign in"
                )}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  Prompt Library Administration
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
